import type { HelpSectionContent } from './types';

// CB:BEGIN homeowner-help-content
export const homeownerHelpContent: HelpSectionContent[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    description: 'Start quickly and understand how CoreBids guides every next step.',
    image: '/help/homeowner/01_landing.png',
    imageAlt: 'Homeowner landing and sign-in view ',
    tags: ['Step-by-step', 'What happens next', 'Notifications'],
    overview: 'CoreBids turns a confusing contractor search into a clear guided flow. You submit one project and follow next actions from your dashboard.',
    steps: [
      { title: 'Create or log into your account', body: 'Use the homeowner entry flow and confirm your contact details.' },
      { title: 'Open your dashboard', body: 'Your dashboard shows your status and exactly what to do next.' },
      { title: 'Use the Help button anytime', body: 'Help is built in to answer questions without leaving your workflow.' },
    ],
    nextStep: 'Create your project so contractors can start preparing bids.',
    notifications: [
      { title: 'Welcome confirmation', body: 'Confirms your account and dashboard access.', type: 'info' },
      { title: 'Action required', body: 'Prompts you if setup details are still missing.', type: 'action' },
    ],
    mistakesToAvoid: ['Skipping profile basics can delay project setup.', 'Ignoring action-required notices slows everything down.'],
    faqs: [
      { question: 'How long does setup take?', answer: 'Most homeowners finish in under 5 minutes.' },
      { question: 'Can I stop and return later?', answer: 'Yes. CoreBids saves your progress.' },
    ],
  },
  {
    id: 'creating-a-project',
    title: 'Creating a Project',
    description: 'Open a new project and define the basic scope.',
    image: '/help/homeowner/04_estimate_form.png',
    imageAlt: 'Project wizard entry and project type selection',
    tags: ['Step-by-step', 'Common issues'],
    overview: 'This is where your request is created. The clearer your setup, the better your bids.',
    steps: [
      { title: 'Start project from dashboard', body: 'Click the primary project action in the homeowner dashboard.' },
      { title: 'Choose project type', body: 'Select the closest match to your work needed.' },
      { title: 'Confirm address details', body: 'Use the exact property address where work is needed.' },
    ],
    nextStep: 'Continue to project details and upload any supporting photos.',
    notifications: [{ title: 'Project created', body: 'Confirms your project is now active in the workflow.', type: 'success' }],
    mistakesToAvoid: ['Using incomplete address details.', 'Choosing the wrong project type.'],
    faqs: [
      { question: 'Can I edit this later?', answer: 'Yes, you can update details from your project flow.' },
      { question: 'Do I need every detail now?', answer: 'No, but better detail usually improves bid quality.' },
    ],
  },
  {
    id: 'submitting-project-details',
    title: 'Submitting Project Details',
    description: 'Complete intake, upload evidence, and submit.',
    image: '/help/homeowner/04_estimate_form.png',
    imageAlt: 'Project detail form with highlighted required fields',
    tags: ['Step-by-step', 'What to click'],
    overview: 'This section gives contractors the context they need to provide useful bids quickly.',
    steps: [
      { title: 'Answer required questions', body: 'Complete timeline, scope, and property details.' },
      { title: 'Upload photos/documents', body: 'Clear images reduce clarifying questions and delays.' },
      { title: 'Review and submit', body: 'Double-check key info before submitting.' },
    ],
    nextStep: 'After submit, CoreBids transitions your project to bid collection.',
    notifications: [
      { title: 'Submission confirmation', body: 'Confirms your project details were received.', type: 'success' },
      { title: 'Missing details', body: 'Requests additional information if needed.', type: 'action' },
    ],
    mistakesToAvoid: ['Uploading blurry photos.', 'Leaving required fields blank.'],
    faqs: [
      { question: 'What photos matter most?', answer: 'Overall exterior, problem areas, and any visible damage.' },
      { question: 'Will this be shared?', answer: 'Only with relevant workflow participants for your project.' },
    ],
  },
  {
    id: 'after-you-submit',
    title: 'After You Submit',
    description: 'Track status while bids are collected.',
    image: '/help/homeowner/04_dashboard_bids_pane.png',
    imageAlt: 'Homeowner dashboard with status and next-step panel',
    tags: ['What happens next', 'Notifications'],
    overview: 'CoreBids coordinates contractor outreach and keeps your dashboard status updated.',
    steps: [
      { title: 'Monitor your status panel', body: 'It updates as your project progresses toward bids.' },
      { title: 'Respond to action-required prompts', body: 'Quick responses reduce overall project latency.' },
    ],
    nextStep: 'You will be notified as soon as bids are available.',
    notifications: [
      { title: 'Status update', body: 'Signals progression through key project states.', type: 'info' },
      { title: 'New bid available', body: 'Notifies you when contractor proposals arrive.', type: 'success' },
    ],
    mistakesToAvoid: ['Waiting too long to respond to clarifying questions.'],
    faqs: [{ question: 'How long until bids arrive?', answer: 'Often within 24 hours depending on project complexity and market conditions.' }],
  },
  {
    id: 'viewing-bids',
    title: 'Viewing Bids',
    description: 'Compare contractors clearly in one place.',
    image: '/help/homeowner/05_bids_list.png',
    imageAlt: 'Bid comparison area with highlighted details and actions',
    tags: ['Step-by-step', 'Common issues'],
    overview: 'Compare bids by scope quality, schedule confidence, and pricing—not just price alone.',
    steps: [
      { title: 'Open bid details', body: 'Review scope notes, assumptions, and timeline.' },
      { title: 'Compare at least top options', body: 'Look for clarity and fit, not only the lowest number.' },
      { title: 'Shortlist your preferred contractor', body: 'Pick the strongest overall value for your goals.' },
    ],
    nextStep: 'Accept the bid that best fits your project needs.',
    notifications: [{ title: 'Bid expiring soon', body: 'Warns you when a bid needs timely action.', type: 'warning' }],
    mistakesToAvoid: ['Selecting only by lowest price.', 'Ignoring scope details and assumptions.'],
    faqs: [{ question: 'Can I ask for changes?', answer: 'Use support or follow platform guidance for clarification steps.' }],
  },
  {
    id: 'selecting-a-contractor',
    title: 'Selecting a Contractor',
    description: 'Accept your chosen bid and move into execution.',
    image: '/help/homeowner/06_bid_proposal_detail.png',
    imageAlt: 'Accept bid call-to-action with confirmation prompt',
    tags: ['What to click', 'What happens next'],
    overview: 'Bid acceptance starts kickoff, payment/deposit steps, and project execution sequencing.',
    steps: [
      { title: 'Click Accept Bid', body: 'Use the accept action on your selected proposal.' },
      { title: 'Confirm your selection', body: 'Review details and confirm in the modal.' },
    ],
    nextStep: 'You will be guided into payment/deposit and milestone kickoff.',
    notifications: [{ title: 'Bid accepted', body: 'Confirms your selection and starts next-stage tasks.', type: 'success' }],
    mistakesToAvoid: ['Accepting before reviewing timeline commitments.'],
    faqs: [{ question: 'Can I change after accepting?', answer: 'Contact support immediately for urgent correction.' }],
  },
  {
    id: 'payments-deposits',
    title: 'Payments & Deposits',
    description: 'Fund your project securely and unlock execution.',
    image: '/help/homeowner/08_project_start_tasks.png',
    imageAlt: 'Payment and deposit screen with highlighted action buttons',
    tags: ['Step-by-step', 'Notifications'],
    overview: 'Payment/deposit completion is a key gate that enables project execution and milestone flow.',
    steps: [
      { title: 'Open payment screen', body: 'Use your next-step banner or payment call-to-action.' },
      { title: 'Complete deposit/payment', body: 'Submit securely and verify confirmation state.' },
    ],
    nextStep: 'Milestone execution tracking becomes active.',
    notifications: [
      { title: 'Payment received', body: 'Confirms your payment state and progression.', type: 'success' },
      { title: 'Payment action required', body: 'Appears if payment is incomplete or interrupted.', type: 'action' },
    ],
    mistakesToAvoid: ['Leaving payment unfinished after starting checkout.'],
    faqs: [{ question: 'Where do I verify payment?', answer: 'Check your dashboard status and escrow/milestone area.' }],
  },
  {
    id: 'project-progress-milestones',
    title: 'Project Progress & Milestones',
    description: 'Review updates and approve milestones confidently.',
    image: '/help/homeowner/10_milestones.png',
    imageAlt: 'Milestone timeline and approval actions',
    tags: ['What happens next', 'Notifications'],
    overview: 'Milestones provide clear checkpoints so you can verify progress before approvals.',
    steps: [
      { title: 'Open milestone view', body: 'Use escrow/milestones from dashboard or project page.' },
      { title: 'Review uploaded proof', body: 'Confirm work aligns with expected milestone outcomes.' },
      { title: 'Approve or flag issues', body: 'Approve when verified, escalate if something is off.' },
    ],
    nextStep: 'Approved milestones advance the project toward completion.',
    notifications: [
      { title: 'Milestone ready', body: 'Requests your review and approval.', type: 'action' },
      { title: 'Milestone approved', body: 'Confirms progression to the next stage.', type: 'success' },
    ],
    mistakesToAvoid: ['Approving without checking evidence.', 'Delaying approvals when evidence is complete.'],
    faqs: [{ question: 'What if work does not match?', answer: 'Use dispute/review pathways and contact support with details.' }],
  },
  {
    id: 'completion',
    title: 'Completion',
    description: 'Close out your project and keep final records.',
    image: '/help/homeowner/11_documents.png',
    imageAlt: 'Project completion and final documents panel',
    tags: ['Step-by-step', 'What happens next'],
    overview: 'Completion confirms your project lifecycle and ensures final documentation is accessible.',
    steps: [
      { title: 'Confirm final work status', body: 'Verify project outcomes before final close.' },
      { title: 'Review project documents', body: 'Open documents and save final records.' },
    ],
    nextStep: 'Your project is marked complete and retained in project history.',
    notifications: [{ title: 'Project completed', body: 'Final completion confirmation.', type: 'success' }],
    mistakesToAvoid: ['Not saving final documents for future reference.'],
    faqs: [{ question: 'Where are final files?', answer: 'In your Documents section and project history views.' }],
  },
  {
    id: 'notifications-explained',
    title: 'Notifications Explained',
    description: 'Understand alerts and which ones need immediate action.',
    image: '/help/homeowner/13_help_notifications.png',
    imageAlt: 'Notification examples and meanings',
    tags: ['Notifications', 'Common issues'],
    overview: 'Notifications keep your project moving. Action-required alerts should be handled first.',
    steps: [
      { title: 'Prioritize Action Required', body: 'These are blocking tasks that pause progress if ignored.' },
      { title: 'Use direct links from notifications', body: 'Jump directly to the correct page/section.' },
    ],
    nextStep: 'Timely action reduces delays and support needs.',
    notifications: [
      { title: 'New bid', body: 'A new contractor proposal is ready.', type: 'info' },
      { title: 'Milestone ready', body: 'Contractor submitted for your review.', type: 'action' },
      { title: 'Completion', body: 'Project reached final state.', type: 'success' },
    ],
    mistakesToAvoid: ['Ignoring action-required messages.', 'Waiting days to review milestone notifications.'],
    faqs: [{ question: 'Where do notifications appear?', answer: 'In-app status surfaces and email notifications.' }],
  },
  {
    id: 'common-questions',
    title: 'Common Questions',
    description: 'Fast answers to frequent homeowner concerns.',
    image: '/help/homeowner/04_dashboard_bids_pane.png',
    imageAlt: 'General homeowner help and FAQ context',
    tags: ['FAQ', 'Support topics'],
    overview: 'Use this section when you are unsure which workflow stage your question belongs to.',
    steps: [
      { title: 'Search by keyword', body: 'Try terms like bid, payment, milestone, or completion.' },
      { title: 'Open linked sections', body: 'Jump directly to the most relevant workflow guidance.' },
    ],
    nextStep: 'If still blocked, contact support with your project ID.',
    notifications: [{ title: 'Support response', body: 'Follow-up from support team via current support channel.', type: 'info' }],
    mistakesToAvoid: ['Opening a ticket before checking relevant step guidance.'],
    faqs: [
      { question: 'Can I pause my project?', answer: 'Contact support to discuss scheduling/exception options.' },
      { question: 'Who can see my project?', answer: 'Only required platform participants and system roles for workflow execution.' },
    ],
  },
  {
    id: 'contact-support',
    title: 'Contact Support',
    description: 'Escalate quickly with complete context.',
    image: '/help/homeowner/04_dashboard_bids_pane.png',
    imageAlt: 'Support CTA and contact guidance',
    tags: ['Support', 'Action required'],
    overview: 'When self-service is not enough, provide complete context so support can resolve quickly.',
    steps: [
      { title: 'Include project ID', body: 'Share the exact project identifier.' },
      { title: 'Describe where you are blocked', body: 'Mention the page/step and expected outcome.' },
      { title: 'Attach screenshot if possible', body: 'Visual context reduces back-and-forth.' },
    ],
    nextStep: 'Support follows up with the fastest path to unblock you.',
    notifications: [{ title: 'Support follow-up', body: 'Reply with requested details promptly.', type: 'action' }],
    mistakesToAvoid: ['Sending vague “it does not work” messages without context.'],
    faqs: [{ question: 'What is the best support format?', answer: 'Project ID + screenshot + step where issue occurs.' }],
  },
];
// CB:END homeowner-help-content

